import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  IconButton,
  Fab,
  Collapse,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';

const LynnIcon = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="-6 -4 56 56" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ display: 'block' }}>
    <path d="M48 24C48 32.8366 40.8366 40 32 40H24L16 48V40C7.16344 40 0 32.8366 0 24C0 15.1634 7.16344 8 16 8H32C40.8366 8 48 15.1634 48 24Z" 
          fill="#0d2c54" 
          stroke="#0a2343" 
          strokeWidth="1.5"/>
    <g opacity="0.6">
      <path d="M12 22C14 20 16 22 18 22C20 22 22 20 24 20C26 20 28 22 30 22C32 22 34 20 36 20" 
            stroke="white" 
            strokeWidth="2" 
            strokeLinecap="round" 
            fill="none"/>
      <path d="M12 28C14 26 16 28 18 28C20 28 22 26 24 26C26 26 28 28 30 28C32 28 34 26 36 26" 
            stroke="white" 
            strokeWidth="2" 
            strokeLinecap="round" 
            fill="none"/>
    </g>
    <circle cx="44" cy="12" r="6" fill="#dbf14e" stroke="white" strokeWidth="1.5"/>
    <path d="M44 9V15M41 12H47" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

import ReactMarkdown from 'react-markdown';
import { useChatBot } from '../context/ChatBotContext';
import { useLocation } from 'react-router-dom';

const DEFAULT_GREETING = 'Hello! I can help answer your questions about Comberton Community Swimming Club membership, fees, duties, and pool rules. What would you like to know?';

const getPageKey = (pathname) => {
  if (pathname === '/') return 'home';
  if (pathname.startsWith('/register')) return 'registration';
  if (pathname.startsWith('/members')) return 'members';
  if (pathname.startsWith('/rules')) return 'rules';
  if (pathname.startsWith('/duties') || pathname.includes('duty')) return 'duties';
  return 'global';
};

const ChatBot = () => {
  const { isOpen, setIsOpen, initialMessage, clearInitialMessage, getPageContent } = useChatBot();
  const location = useLocation();
  const currentPage = getPageKey(location.pathname);
  const [greeting, setGreeting] = useState(DEFAULT_GREETING);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: DEFAULT_GREETING,
    },
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isActiveForPage, setIsActiveForPage] = useState(true);
  const [isWithinBudget, setIsWithinBudget] = useState(true);
  const messagesEndRef = useRef(null);
  const hasProcessedInitialMessage = useRef(false);
  const lastFetchedPage = useRef(null);

  const checkBudgetStatus = useCallback(() => {
    fetch('/api/chatbot/status')
      .then(res => res.json())
      .then(data => {
        setIsWithinBudget(data.available !== false);
      })
      .catch(() => {
        setIsWithinBudget(true);
      });
  }, []);

  useEffect(() => {
    checkBudgetStatus();
  }, [checkBudgetStatus]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (currentPage !== lastFetchedPage.current) {
      lastFetchedPage.current = currentPage;
      fetch(`/api/chatbot/greeting/${currentPage}`)
        .then(res => res.json())
        .then(data => {
          setIsActiveForPage(data.isActive !== false);
          const newGreeting = data.greeting || DEFAULT_GREETING;
          setGreeting(newGreeting);
          setMessages([{ role: 'assistant', content: newGreeting }]);
        })
        .catch(() => {
          setIsActiveForPage(true);
        });
    }
  }, [currentPage]);

  const sendMessage = useCallback(async (userMessage) => {
    setMessages((prev) => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);

    try {
      const conversationHistory = messages.map((m) => ({
        role: m.role,
        content: m.content,
      }));

      const headers = { 'Content-Type': 'application/json' };
      const adminToken = sessionStorage.getItem('adminToken');
      if (adminToken) {
        headers['Authorization'] = `Bearer ${adminToken}`;
      }

      const pageContent = getPageContent();
      const response = await fetch('/api/chatbot', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          message: userMessage,
          conversationHistory,
          page: currentPage,
          pageContent: pageContent || undefined,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setMessages((prev) => [
          ...prev,
          { role: 'assistant', content: data.reply },
        ]);
        checkBudgetStatus();
      } else {
        setMessages((prev) => [
          ...prev,
          {
            role: 'assistant',
            content: 'Sorry, I encountered an error. Please try again.',
          },
        ]);
      }
    } catch (error) {
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: 'Sorry, I could not connect to the server. Please try again later.',
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  }, [messages, currentPage, checkBudgetStatus, getPageContent]);

  useEffect(() => {
    if (initialMessage && isOpen && !hasProcessedInitialMessage.current) {
      hasProcessedInitialMessage.current = true;
      sendMessage(initialMessage);
      clearInitialMessage();
    }
  }, [initialMessage, isOpen, sendMessage, clearInitialMessage]);

  useEffect(() => {
    if (!isOpen) {
      hasProcessedInitialMessage.current = false;
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;

    const userMessage = input.trim();
    setInput('');
    await sendMessage(userMessage);
  };

  if (!isActiveForPage || !isWithinBudget) {
    return null;
  }

  return (
    <>
      <Fab
        onClick={() => setIsOpen(!isOpen)}
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          bgcolor: '#dbf14e',
          color: '#121212',
          '&:hover': {
            bgcolor: '#c5d943',
          },
          zIndex: 1000,
        }}
      >
        {isOpen ? <CloseIcon /> : <LynnIcon size={28} />}
      </Fab>

      <Collapse in={isOpen}>
        <Paper
          elevation={8}
          sx={{
            position: 'fixed',
            bottom: 88,
            right: 24,
            width: 360,
            maxWidth: 'calc(100vw - 48px)',
            height: 480,
            maxHeight: 'calc(100vh - 120px)',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 3,
            overflow: 'hidden',
            zIndex: 1000,
          }}
        >
          <Box
            sx={{
              bgcolor: '#0d2c54',
              color: 'white',
              p: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <LynnIcon size={24} />
            <Typography variant="h6" sx={{ fontFamily: 'Chillax, sans-serif' }}>
              Ask Lynn
            </Typography>
          </Box>

          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              overscrollBehavior: 'contain',
              WebkitOverflowScrolling: 'touch',
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
              bgcolor: '#f5f5f5',
            }}
          >
            {messages.map((message, index) => (
              <Box
                key={index}
                sx={{
                  alignSelf:
                    message.role === 'user' ? 'flex-end' : 'flex-start',
                  maxWidth: '85%',
                }}
              >
                <Paper
                  sx={{
                    p: 1.5,
                    bgcolor:
                      message.role === 'user' ? '#0d2c54' : 'white',
                    color: message.role === 'user' ? 'white' : '#121212',
                    borderRadius: 2,
                    borderTopRightRadius:
                      message.role === 'user' ? 0 : 16,
                    borderTopLeftRadius:
                      message.role === 'user' ? 16 : 0,
                    '& p': { m: 0, mb: 1 },
                    '& p:last-child': { mb: 0 },
                    '& ul, & ol': { m: 0, pl: 2, mb: 1 },
                    '& li': { mb: 0.5 },
                    '& strong': { fontWeight: 600 },
                  }}
                >
                  <Typography variant="body2" component="div">
                    <ReactMarkdown>{message.content}</ReactMarkdown>
                  </Typography>
                </Paper>
              </Box>
            ))}
            {isLoading && (
              <Box sx={{ alignSelf: 'flex-start' }}>
                <Paper
                  sx={{
                    p: 1.5,
                    bgcolor: 'white',
                    borderRadius: 2,
                    borderTopLeftRadius: 0,
                  }}
                >
                  <CircularProgress size={20} />
                </Paper>
              </Box>
            )}
            <div ref={messagesEndRef} />
          </Box>

          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
              p: 2,
              bgcolor: 'white',
              borderTop: '1px solid #e0e0e0',
              display: 'flex',
              gap: 1,
            }}
          >
            <TextField
              fullWidth
              size="small"
              placeholder="Ask a question..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              disabled={isLoading}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
            />
            <IconButton
              type="submit"
              disabled={!input.trim() || isLoading}
              sx={{
                bgcolor: '#dbf14e',
                color: '#121212',
                '&:hover': {
                  bgcolor: '#c5d943',
                },
                '&:disabled': {
                  bgcolor: '#e0e0e0',
                },
              }}
            >
              <SendIcon />
            </IconButton>
          </Box>
        </Paper>
      </Collapse>
    </>
  );
};

export default ChatBot;
